import { generateCalendarToken, getCalendarToken } from "@/api/auth";
import { updateMember } from "@/api/member";
import { fetchMyTeams } from "@/api/team";
import { deleteWebhookToken, generateWebhookToken, getWebhookToken } from "@/api/webhookToken";
import { useAuthStore } from "@/app/store/useAuthStore";
import { useTeamStore } from "@/app/store/useTeamStore";
import { Button } from "@/shared/components/ui/button";
import { toast } from "@/shared/hooks/useToast";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Copy, RefreshCw, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || "http://localhost:8000";

export function SettingsPage() {
  const qc = useQueryClient();
  const { grant, memberId, phone: storedPhone, setPhone: setStoredPhone } = useAuthStore();
  const { defaultTeamId, setDefaultTeamId } = useTeamStore();
  const isAdmin = grant === "admin";

  const [phone, setPhone] = useState(storedPhone ?? "");
  const [pendingDefaultTeamId, setPendingDefaultTeamId] = useState<number | null>(defaultTeamId);
  const [calendarTeamId, setCalendarTeamId] = useState<number | null>(defaultTeamId);
  const [copied, setCopied] = useState(false);
  const [webhookTokenCopied, setWebhookTokenCopied] = useState(false);
  const [webhookUrlCopied, setWebhookUrlCopied] = useState(false);
  const [webhookTeamId, setWebhookTeamId] = useState<number | null>(defaultTeamId);
  const [generatedWebhookToken, setGeneratedWebhookToken] = useState<string | null>(null);
  const [storedCalToken, setStoredCalToken] = useState<string | null>(
    () => localStorage.getItem("cal_token"),
  );

  useEffect(() => { setPhone(storedPhone ?? ""); }, [storedPhone]);
  useEffect(() => {
    setPendingDefaultTeamId(defaultTeamId);
    setCalendarTeamId(defaultTeamId);
    setWebhookTeamId(defaultTeamId);
  }, [defaultTeamId]);

  const { data: teams = [], isLoading: teamsLoading } = useQuery({
    queryKey: ["my-teams"],
    queryFn: fetchMyTeams,
  });

  const { data: calendarTokenData } = useQuery({
    queryKey: ["calendar-token"],
    queryFn: getCalendarToken,
  });

  const hasToken = calendarTokenData?.has_token ?? false;

  const phoneMut = useMutation({
    mutationFn: () => {
      if (!memberId) throw new Error("memberId 없음");
      return updateMember(memberId, { phone: phone.trim() });
    },
    onSuccess: (res) => {
      // store 값이 같으면 effect 가 안 돌므로 서버가 정규화한 값을 입력란에 직접 반영
      setPhone(res.phone ?? "");
      setStoredPhone(res.phone);
      toast({ title: "전화번호 저장 완료" });
    },
    onError: (error: Error) => {
      toast({
        variant: "destructive",
        title: "전화번호 저장 실패",
        description: error.message || "전화번호 저장 중 오류가 발생했습니다.",
      });
    },
  });

  const regenerateMut = useMutation({
    mutationFn: generateCalendarToken,
    onSuccess: (res) => {
      if (res.calendar_token) {
        localStorage.setItem("cal_token", res.calendar_token);
        setStoredCalToken(res.calendar_token);
      }
      qc.invalidateQueries({ queryKey: ["calendar-token"] });
      toast({ title: "캘린더 토큰이 재발급되었습니다." });
    },
    onError: (error: Error) => {
      toast({ variant: "destructive", title: "재발급 실패", description: error.message || "알 수 없는 오류가 발생했습니다." });
    },
  });

  const { data: webhookTokenData } = useQuery({
    queryKey: ["webhook-token", webhookTeamId],
    queryFn: () => getWebhookToken(webhookTeamId!),
    enabled: isAdmin && webhookTeamId != null,
  });

  const webhookHasToken = webhookTokenData?.has_token ?? false;

  const generateWebhookMut = useMutation({
    mutationFn: () => {
      if (!webhookTeamId) throw new Error("팀을 선택해 주세요.");
      return generateWebhookToken(webhookTeamId);
    },
    onSuccess: (res) => {
      setGeneratedWebhookToken(res.webhook_token ?? null);
      qc.invalidateQueries({ queryKey: ["webhook-token", webhookTeamId] });
      toast({ title: "웹훅 토큰이 발급되었습니다. 토큰을 복사해 두세요." });
    },
    onError: (error: Error) => {
      toast({ variant: "destructive", title: "웹훅 토큰 발급 실패", description: error.message });
    },
  });

  const deleteWebhookMut = useMutation({
    mutationFn: () => {
      if (!webhookTeamId) throw new Error("팀을 선택해 주세요.");
      return deleteWebhookToken(webhookTeamId);
    },
    onSuccess: () => {
      setGeneratedWebhookToken(null);
      qc.invalidateQueries({ queryKey: ["webhook-token", webhookTeamId] });
      toast({ title: "웹훅 토큰이 삭제되었습니다." });
    },
    onError: (error: Error) => {
      toast({ variant: "destructive", title: "웹훅 토큰 삭제 실패", description: error.message });
    },
  });

  function getWebhookUrl() {
    if (!webhookTeamId) return null;
    return `${API_BASE_URL}/team/${webhookTeamId}/incident`;
  }

  function handleCopyWebhookUrl() {
    const url = getWebhookUrl();
    if (!url) return;
    navigator.clipboard.writeText(url).then(() => {
      setWebhookUrlCopied(true);
      setTimeout(() => setWebhookUrlCopied(false), 2000);
    }).catch(() => {
      toast({ variant: "destructive", title: "복사 실패", description: "클립보드 접근이 거부되었습니다." });
    });
  }

  function handleCopyWebhookToken() {
    if (!generatedWebhookToken) return;
    navigator.clipboard.writeText(generatedWebhookToken).then(() => {
      setWebhookTokenCopied(true);
      setTimeout(() => setWebhookTokenCopied(false), 2000);
    }).catch(() => {
      toast({ variant: "destructive", title: "복사 실패", description: "클립보드 접근이 거부되었습니다." });
    });
  }

  function handleSaveDefaultTeam() {
    setDefaultTeamId(pendingDefaultTeamId);
    toast({ title: "기본 팀 저장 완료" });
  }

  function getCalendarUrl() {
    if (!calendarTeamId || !storedCalToken) return null;
    const httpUrl = `${API_BASE_URL}/team/${calendarTeamId}/calendar?cal_token=${storedCalToken}`;
    return httpUrl.replace(/^https?/, "webcal");
  }

  function handleCopyCalendarUrl() {
    const url = getCalendarUrl();
    if (!url) return;
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => {
      toast({ variant: "destructive", title: "복사 실패", description: "클립보드 접근이 거부되었습니다. URL을 직접 복사해 주세요." });
    });
  }

  const calendarUrl = getCalendarUrl();

  return (
    <div className="p-6 max-w-lg">
      <h1 className="text-xl font-bold mb-6">설정</h1>

      {/* Default Team */}
      <section className="mb-8">
        <h2 className="text-base font-semibold mb-1">기본 팀</h2>
        <p className="text-sm text-muted-foreground mb-3">
          앱 시작 시 자동으로 선택될 팀을 지정합니다.
        </p>
        <div className="flex gap-2">
          <select
            value={pendingDefaultTeamId ?? ""}
            disabled={teamsLoading}
            onChange={(e) => setPendingDefaultTeamId(e.target.value ? Number(e.target.value) : null)}
            className="flex-1 border rounded px-3 py-2 text-sm bg-background focus:outline-none focus:ring-1 focus:ring-ring disabled:opacity-50"
          >
            <option value="">팀 선택 안 함</option>
            {teams.map((team) => (
              <option key={team.id} value={team.id}>{team.name}</option>
            ))}
          </select>
          <Button onClick={handleSaveDefaultTeam}>저장</Button>
        </div>
      </section>

      {/* Phone */}
      <section className="mb-8">
        <h2 className="text-base font-semibold mb-1">전화번호</h2>
        <p className="text-sm text-muted-foreground mb-3">
          온콜 알림 수신에 사용되는 전화번호입니다. 저장하면 국제 형식(+821012345678)으로 변환됩니다.
        </p>
        <div className="flex gap-2">
          <input
            type="text"
            placeholder="010-0000-0000"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && !phoneMut.isPending && phoneMut.mutate()}
            className="flex-1 border rounded px-3 py-2 text-sm bg-background focus:outline-none focus:ring-1 focus:ring-ring"
          />
          <Button disabled={phoneMut.isPending} onClick={() => phoneMut.mutate()}>
            {phoneMut.isPending ? "저장 중…" : "저장"}
          </Button>
        </div>
      </section>

      {/* Calendar Subscription */}
      <section className="mb-8">
        <h2 className="text-base font-semibold mb-1">Google 캘린더 구독</h2>
        <p className="text-sm text-muted-foreground mb-3">
          팀의 온콜 스케줄을 Google 캘린더에 자동 동기화합니다.
          URL을 복사 후 Google 캘린더 → 다른 캘린더 추가 → URL로 추가에 붙여넣으세요.
        </p>

        {/* Token 발급 */}
        {!hasToken ? (
          <Button
            variant="outline"
            disabled={regenerateMut.isPending}
            onClick={() => regenerateMut.mutate()}
            className="mb-3"
          >
            <RefreshCw className={`h-4 w-4 mr-1${regenerateMut.isPending ? " animate-spin" : ""}`} />
            캘린더 토큰 발급
          </Button>
        ) : (
          <div className="flex items-center gap-2 mb-3">
            <span className="text-xs text-muted-foreground font-mono bg-muted px-3 py-1.5 rounded flex-1">
              토큰: ••••••••
            </span>
            <Button
              variant="ghost"
              size="sm"
              disabled={regenerateMut.isPending}
              onClick={() => regenerateMut.mutate()}
              className="text-xs text-muted-foreground"
            >
              <RefreshCw className={`h-3.5 w-3.5 mr-1${regenerateMut.isPending ? " animate-spin" : ""}`} />
              재발급
            </Button>
          </div>
        )}

        {/* 팀 선택 + URL 복사 */}
        {hasToken && (
          <>
            <div className="flex gap-2 mb-2">
              <select
                value={calendarTeamId ?? ""}
                disabled={teamsLoading}
                onChange={(e) => setCalendarTeamId(e.target.value ? Number(e.target.value) : null)}
                className="flex-1 border rounded px-3 py-2 text-sm bg-background focus:outline-none focus:ring-1 focus:ring-ring disabled:opacity-50"
              >
                <option value="">팀 선택</option>
                {teams.map((team) => (
                  <option key={team.id} value={team.id}>{team.name}</option>
                ))}
              </select>
              <Button
                variant="outline"
                disabled={!calendarUrl}
                onClick={handleCopyCalendarUrl}
                className="shrink-0"
              >
                {copied ? (
                  <><Check className="h-4 w-4 mr-1" />복사됨</>
                ) : (
                  <><Copy className="h-4 w-4 mr-1" />URL 복사</>
                )}
              </Button>
            </div>
            {calendarUrl && (
              <p className="text-xs text-muted-foreground break-all font-mono bg-muted px-3 py-2 rounded">
                {calendarUrl.replace(/cal_token=.*$/, "cal_token=••••••••")}
              </p>
            )}
            <p className="text-xs text-muted-foreground mt-2">
              ⚠️ 이 URL에는 캘린더 전용 토큰이 포함되어 있습니다. 토큰이 유출되면 재발급하세요.
            </p>
          </>
        )}
      </section>

      {/* Webhook Token — admin only */}
      {isAdmin && (
        <section>
          <h2 className="text-base font-semibold mb-1">웹훅 토큰 관리</h2>
          <p className="text-sm text-muted-foreground mb-3">
            외부 서비스(Datadog, Grafana 등)에서 인시던트를 트리거할 때 사용하는 웹훅 인증 토큰입니다.
            팀 오너만 발급/삭제할 수 있습니다.
          </p>

          <div className="flex gap-2 mb-3">
            <select
              value={webhookTeamId ?? ""}
              disabled={teamsLoading}
              onChange={(e) => {
                setWebhookTeamId(e.target.value ? Number(e.target.value) : null);
                setGeneratedWebhookToken(null);
              }}
              className="flex-1 border rounded px-3 py-2 text-sm bg-background focus:outline-none focus:ring-1 focus:ring-ring disabled:opacity-50"
            >
              <option value="">팀 선택</option>
              {teams.map((team) => (
                <option key={team.id} value={team.id}>{team.name}</option>
              ))}
            </select>
          </div>

          {webhookTeamId != null && (
            <>
              {!webhookHasToken && !generatedWebhookToken ? (
                <Button
                  variant="outline"
                  disabled={generateWebhookMut.isPending}
                  onClick={() => generateWebhookMut.mutate()}
                >
                  <RefreshCw className={`h-4 w-4 mr-1${generateWebhookMut.isPending ? " animate-spin" : ""}`} />
                  웹훅 토큰 발급
                </Button>
              ) : (
                <div className="space-y-3">
                  {/* Webhook URL */}
                  <div>
                    <span className="text-xs font-medium text-muted-foreground">요청 URL</span>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs font-mono bg-muted px-3 py-1.5 rounded flex-1 break-all">
                        {getWebhookUrl()}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleCopyWebhookUrl}
                        className="shrink-0"
                      >
                        {webhookUrlCopied ? (
                          <><Check className="h-3.5 w-3.5 mr-1" />복사됨</>
                        ) : (
                          <><Copy className="h-3.5 w-3.5 mr-1" />URL 복사</>
                        )}
                      </Button>
                    </div>
                  </div>

                  {/* Webhook Token */}
                  <div>
                    <span className="text-xs font-medium text-muted-foreground">인증 토큰</span>
                    {generatedWebhookToken ? (
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs font-mono bg-muted px-3 py-1.5 rounded flex-1 break-all">
                          {generatedWebhookToken}
                        </span>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleCopyWebhookToken}
                          className="shrink-0"
                        >
                          {webhookTokenCopied ? (
                            <><Check className="h-3.5 w-3.5 mr-1" />복사됨</>
                          ) : (
                            <><Copy className="h-3.5 w-3.5 mr-1" />복사</>
                          )}
                        </Button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs text-muted-foreground font-mono bg-muted px-3 py-1.5 rounded flex-1">
                          토큰: ••••••••
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={generateWebhookMut.isPending}
                      onClick={() => {
                        if (!window.confirm("기존 토큰이 무효화됩니다. 재발급하시겠습니까?")) return;
                        generateWebhookMut.mutate();
                      }}
                      className="text-xs text-muted-foreground"
                    >
                      <RefreshCw className={`h-3.5 w-3.5 mr-1${generateWebhookMut.isPending ? " animate-spin" : ""}`} />
                      재발급
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={deleteWebhookMut.isPending}
                      onClick={() => {
                        if (!window.confirm("웹훅 토큰을 삭제하시겠습니까? 삭제 후 웹훅 인증이 불가합니다.")) return;
                        deleteWebhookMut.mutate();
                      }}
                      className="text-xs text-destructive hover:text-destructive"
                    >
                      <Trash2 className="h-3.5 w-3.5 mr-1" />
                      삭제
                    </Button>
                  </div>
                  {generatedWebhookToken && (
                    <p className="text-xs text-destructive">
                      이 토큰은 지금만 확인할 수 있습니다. 반드시 복사해 두세요.
                    </p>
                  )}
                  <p className="text-xs text-muted-foreground">
                    ⚠️ 요청 시 <code className="bg-muted px-1 py-0.5 rounded">X-Webhook-Token</code> 헤더에 토큰을 포함하세요.
                  </p>
                </div>
              )}
            </>
          )}
        </section>
      )}
    </div>
  );
}
